// Drop-zone + client preview + upload, with backend error code mapping.
//
// Client-side validation happens twice:
//   (1) react-dropzone enforces accept + maxSize at the FS layer
//       (rejects e.g. .json, >5 MiB without ever touching axios).
//   (2) papaparse preview (first 50 rows, in a Worker) tells the user
//       the column shape BEFORE they click upload.
// The backend re-validates everything authoritatively; we only use the
// preview to short-circuit obvious mistakes.

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useUploadDataset } from '@/hooks/useDatasets';
import { extractApiError } from '@/lib/api';
import type { Dataset } from '@/lib/datasets';
import { friendlyError } from '@/lib/error-messages';
import { AlertCircle, FileText, UploadCloud, X } from 'lucide-react';
import Papa from 'papaparse';
import * as React from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

const MAX_BYTES = 5 * 1024 * 1024; // 5 MiB — backend's MAX_INLINE_BYTES.
const PREVIEW_ROWS = 50;

interface PreviewState {
  columns: string[];
  rows: string[][];
  rowCountApprox: number; // counted from the preview only — not authoritative
}

interface CsvUploadProps {
  onUploaded?: (dataset: Dataset) => void;
  // When true, hides the "Name" override input — used in the inline-upload
  // path on /discoveries/new where the user is in a hurry.
  hideNameField?: boolean;
}

export function CsvUpload({ onUploaded, hideNameField = false }: CsvUploadProps) {
  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<PreviewState | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [name, setName] = React.useState('');
  const [progress, setProgress] = React.useState(0);

  const upload = useUploadDataset({
    onSuccess: ({ dataset, created }) => {
      // Backend dedupes on (org_id, sha256): a re-upload returns the existing
      // row with HTTP 200 (created=false). Tell the user instead of pretending
      // we created something new — they'll find the row already in the table
      // (possibly with a different name) and otherwise think the upload silently
      // failed.
      if (created) {
        toast.success(
          `Uploaded "${dataset.name}" — ${dataset.n_rows} rows × ${dataset.n_cols} cols`,
        );
      } else {
        toast.info(
          `"${dataset.name}" is already in your library — same contents uploaded earlier.`,
        );
      }
      onUploaded?.(dataset);
      reset();
    },
    onError: (err) => {
      const apiErr = extractApiError(err);
      const code = apiErr?.code;
      // Multi-error envelope from csv_validation: metadata.errors is an array.
      const metaErrors = (
        apiErr?.metadata as { errors?: Array<{ code: string; message: string }> } | undefined
      )?.errors;
      if (code === 'dataset.validation_failed' && metaErrors?.length) {
        const lines = metaErrors.map((e) => friendlyError(e.code, e.message));
        toast.error(lines.join(' • '));
        return;
      }
      toast.error(friendlyError(code, apiErr?.message || 'Upload failed.'));
    },
  });

  function reset() {
    setFile(null);
    setPreview(null);
    setPreviewError(null);
    setName('');
    setProgress(0);
  }

  const onDrop = React.useCallback((accepted: File[], rejected: FileRejection[]) => {
    if (rejected.length > 0) {
      const r = rejected[0].errors[0];
      const map: Record<string, string> = {
        'file-too-large': 'File must be 5MB or smaller.',
        'file-invalid-type': 'Only CSV files are supported.',
        'too-many-files': 'Upload one file at a time.',
      };
      toast.error(map[r.code] ?? r.message);
      return;
    }
    const f = accepted[0];
    if (!f) return;
    setFile(f);
    setName(f.name);
    setPreview(null);
    setPreviewError(null);

    Papa.parse<string[]>(f, {
      worker: true,
      preview: PREVIEW_ROWS + 1, // +1 to grab the header
      skipEmptyLines: true,
      complete: (result) => {
        const rows = result.data ?? [];
        if (rows.length === 0) {
          setPreviewError('CSV looks empty.');
          return;
        }
        const [header, ...body] = rows;
        setPreview({
          columns: header,
          rows: body.slice(0, PREVIEW_ROWS),
          rowCountApprox: body.length,
        });
      },
      error: (err) => setPreviewError(err.message || "Couldn't parse this file."),
    });
  }, []);

  const dropzone = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'application/csv': ['.csv'], 'text/plain': ['.csv'] },
    maxSize: MAX_BYTES,
    multiple: false,
    disabled: upload.isPending,
  });

  async function submit() {
    if (!file) return;
    setProgress(0);
    upload.mutate({
      file,
      options: {
        name: name && name !== file.name ? name : undefined,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      },
    });
  }

  return (
    <div className="space-y-4">
      {!file ? (
        <div
          {...dropzone.getRootProps()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-10 text-center transition-colors ${
            dropzone.isDragActive
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:bg-muted/30'
          }`}
        >
          <input {...dropzone.getInputProps()} />
          <UploadCloud className="mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">
            {dropzone.isDragActive ? 'Drop the file here' : 'Drag a CSV here or click to browse'}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Numeric columns only · max 5MB</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border bg-muted/30 p-3">
            <div className="flex min-w-0 items-center gap-2">
              <FileText className="h-4 w-4 shrink-0" />
              <span className="truncate text-sm font-medium">{file.name}</span>
              <span className="text-xs text-muted-foreground">
                {(file.size / 1024).toFixed(1)} KB
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={reset}
              disabled={upload.isPending}
              aria-label="Remove file"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {!hideNameField && (
            <div className="space-y-1.5">
              <Label htmlFor="dataset-name">Name</Label>
              <Input
                id="dataset-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="dataset.csv"
                disabled={upload.isPending}
              />
            </div>
          )}

          {previewError ? (
            <Alert variant="destructive" className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="text-sm">{previewError}</span>
            </Alert>
          ) : preview ? (
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">
                {preview.columns.length} columns · showing first {preview.rows.length} rows
              </div>
              <div className="max-h-64 overflow-auto rounded-md border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 text-left">
                    <tr>
                      {preview.columns.map((c) => (
                        <th key={c} className="whitespace-nowrap px-2 py-1.5 font-medium">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: preview rows are inert + ephemeral
                      <tr key={i} className="border-t">
                        {row.map((cell, j) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: same
                          <td key={j} className="whitespace-nowrap px-2 py-1">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Parsing preview…</p>
          )}

          {upload.isPending && progress > 0 ? <Progress value={progress} /> : null}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={reset} disabled={upload.isPending}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={upload.isPending || !!previewError}>
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
